import { Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { SwiperModule } from 'swiper/angular';
import { SwiperOptions } from 'swiper';
import { CsIconModule } from '@components/icon';
import { ButtonModule } from '@components/button';
import {ButtonCtaComponent} from '@components/button-cta/button-cta.component';
import { FirebaseAndBranchEvent } from '@services/event-logger-service/event-logger.constant';
import { EventLoggerService } from '@services/event-logger-service';
import { ConnectionService } from '@services/connection-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { moveWashCareProductsToEnd } from 'client/helper/regimen-helper';

interface ExtraImageWithTag {
  title?: string;
  content?: string;
  imageLink: string;
  tag: string;
  priority: number;
}

interface SliderProduct {
  product: {
    objectId: string;
    title?: string;
    hiddenTitle?: string;
    rebrandedImageWithBackground?: string;
    quantity?: string;
    quantityUnit?: string;
    purposeInEn?: string;
    extraImagesWithTags: ExtraImageWithTag[];
  };
  productName?: string;
}

interface SliderRegimen {
  class?: string;
  regimenId?: string;
  displayConcern?: { name?: string };
}

interface SlideView {
  objectId: string;
  product: SliderProduct;
  imageLink: string;
  isTagged: boolean;
  title: string;
  subtitle?: string;
  quantityLabel: string;
}

@Component({
  selector: 'app-regimen-products-slider',
  templateUrl: './regimen-products-slider.html',
  imports: [SwiperModule, CsIconModule, ButtonModule, ButtonCtaComponent],
})
export class RegimenProductsSliderComponent {
  private static readonly REGIMEN_SLIDER_TAG = 'regimenSlider';

  private readonly eventLogger = inject(EventLoggerService);
  private readonly connectionService = inject(ConnectionService);
  private readonly experimentService = inject(ExperimentService);

  regimen = input.required<SliderRegimen>();
  products = input.required<SliderProduct[]>();

  /** Hair wash-care products move to the end — mirrors the regimen product-details page. */
  private readonly orderedProducts = computed<SliderProduct[]>(() => {
    const products = this.products();
    if (!this.experimentService.shouldReorderWashCareProducts(this.regimen().class)) return products;
    return moveWashCareProductsToEnd(products);
  });

  readonly slides = computed<SlideView[]>(() => this.orderedProducts().map((product) => this.toSlideView(product)));

  private readonly router = inject(Router);

  readonly swiperConfig: SwiperOptions = {
    slidesPerView: 1.43,
    spaceBetween: 0,
    pagination: false,
    navigation: false,
    autoplay: false,
  };

  goToInstructions(): void {
    this.trackEventAndLogCleverTap(FirebaseAndBranchEvent.REGIMEN_VIEW_SEE_INSTRUCTION, 'regimen-see-instructions');
    this.router.navigate(['/regimen/instructions'], {
      queryParams: this.routeQueryParams(),
    });
  }

  trackEventAndLogCleverTap(event: FirebaseAndBranchEvent, eventName: string): void {
    const user = this.connectionService.getCurrentUser();
    this.eventLogger.trackEventAndLogCleverTapForClass(event, this.regimen().class, eventName, { username: user.get('username') });
  }

  goToProductDetails(product: SliderProduct): void {
    this.trackEventAndLogCleverTap(FirebaseAndBranchEvent.REGIMEN_VIEW_READ_ABOUT_PRODUCTS, 'products');
    this.router.navigate(['/user/regimen/products'], {
      queryParams: { ...this.routeQueryParams(), productId: product.product.objectId },
    });
  }

  private routeQueryParams(): { class: string; regimenId: string } {
    const regimen = this.regimen();
    return { class: regimen.class, regimenId: regimen.regimenId };
  }

  private toSlideView(product: SliderProduct): SlideView {
    const { product: details, productName } = product;
    const quantityLabel = [details.quantity, details.quantityUnit].filter(Boolean).join(' ');
    const sliderImage = this.pickRegimenSliderImage(details.extraImagesWithTags);

    if (sliderImage) {
      return {
        objectId: details.objectId,
        product,
        imageLink: sliderImage.imageLink,
        isTagged: true,
        title: this.resolveTitle(sliderImage.title),
        quantityLabel,
      };
    }

    return {
      objectId: details.objectId,
      product,
      imageLink: details.rebrandedImageWithBackground,
      isTagged: false,
      title: this.resolveTitle(details.title || productName),
      subtitle: details.hiddenTitle,
      quantityLabel,
    };
  }

  private resolveTitle(title: string | undefined): string {
    const concern = this.regimen().displayConcern?.name;
    if (!title || !concern) {
      return title ?? '';
    }
    return title.replaceAll('<primary concern>', concern);
  }

  private pickRegimenSliderImage(extraImagesWithTags: ExtraImageWithTag[]): ExtraImageWithTag | undefined {
    if (!extraImagesWithTags?.length) {
      return undefined;
    }

    return extraImagesWithTags
      .filter((image) => image.tag === RegimenProductsSliderComponent.REGIMEN_SLIDER_TAG)
      .reduce<ExtraImageWithTag | undefined>(
        (highest, image) => (!highest || image.priority > highest.priority ? image : highest),
        undefined,
      );
  }
}
